/**
 * Вход с телефона: одна вкладка, буфер в жесте, вторая нога — очередь.
 * Desktop `enter` не трогаем.
 */

import { useAccounts } from '../accounts';
import { bufferOf, going, hrefOf, legsOf, pasteOf } from '../act';
import { matchTitle } from '../matchTitle';
import { venueName } from '../meta';
import { useScreen } from '../store';
import { copyPhoneStart } from './clipboard';
import { bumpCopy, bumpDoor, bumpFail } from './haptic';
import { type PhoneLegJob, usePhoneQueue } from './queue';
import { openBookLink } from './telegram';
import { urlForPhone } from './urlForPhone';
import type { FeedRow } from '../rows';
import type { RowLeg } from '../../lib/row';

export function hrefOfPhone(id: string, leg: RowLeg, preferWeb = false): string | null {
  const desk = hrefOf(id, leg);
  if (desk === null) return null;
  return urlForPhone(leg.source, desk, { webOnly: preferWeb }) ?? desk;
}

function jobOf(row: FeedRow, leg: RowLeg, index: 1 | 2 | 3): PhoneLegJob {
  return {
    rowId: row.id,
    match: matchTitle(row),
    source: leg.source,
    book: venueName(useScreen.getState().meta, leg.source) || leg.source,
    index,
    stake: pasteOf(leg),
    odds: String(leg.odds),
    promised: String(row.row.roiPct),
  };
}

const held = new Map<string, FeedRow>();

/**
 * Одна нога. `window.open` до любого await. Вторую вкладку не открываем.
 */
export function phoneEnter(row: FeedRow, index: 1 | 2 | 3): boolean {
  const legs = legsOf(row);
  const leg = legs[index - 1];
  if (leg === undefined) return true;

  const web = usePhoneQueue.getState().webOnly;
  const url = hrefOfPhone(row.id, leg, web);
  const text = bufferOf(row, leg, url !== null ? [leg] : []);
  void copyPhoneStart(text).then((ok) => {
    if (ok) {
      bumpCopy();
      usePhoneQueue.getState().say(`сумма в буфере · ${text}`);
    } else {
      bumpFail();
      usePhoneQueue.getState().say('буфер не взялся — скопируйте сумму пальцем');
    }
  });

  let ok = true;
  if (url !== null) {
    ok = openBookLink(url);
    if (ok) bumpDoor();
    else {
      bumpFail();
      usePhoneQueue.getState().say('браузер не пустил окно — разрешите всплывающие');
    }
  } else {
    usePhoneQueue.getState().say('ссылки на матч нет — ищите руками, сумма в буфере');
  }

  const steps = useScreen.getState().steps[row.id];
  if (steps === undefined || steps.opened[index - 1] === null) going(row, index);
  useAccounts.getState().noteUse(leg.source);

  const rest: PhoneLegJob[] = [];
  legs.forEach((one, i) => {
    if (i === index - 1) return;
    rest.push(jobOf(row, one, (i + 1) as 1 | 2 | 3));
  });
  held.set(row.id, row);
  usePhoneQueue.getState().clear();
  if (rest.length > 0) {
    usePhoneQueue.getState().push(rest);
    usePhoneQueue.getState().say('вернись → нога 2');
  }
  return ok;
}

export function phoneEnterQueued(): boolean {
  const next = usePhoneQueue.getState().shift();
  if (next === undefined) return false;
  const row = held.get(next.rowId);
  if (row === undefined) {
    usePhoneQueue.getState().say('вилка уже ушла из кадра');
    return false;
  }
  return phoneEnter(row, next.index);
}
